import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, MousePointerClick, Clock, ArrowDownRight, CheckSquare, Heart, Smartphone, TrendingUp } from 'lucide-react';
import { InteriorSlide, SlideHeader } from './SlideShell';
import type { SlideProps } from './SlideShell';
export function Slide13Optimization({
  index
}: SlideProps) {
  const metrics = [{
    icon: MousePointerClick,
    label: 'Click-through rate'
  }, {
    icon: CheckSquare,
    label: 'Completion rate'
  }, {
    icon: Clock,
    label: 'Average view time'
  }, {
    icon: ArrowDownRight,
    label: 'Drop-off points'
  }, {
    icon: BarChart3,
    label: 'Quiz and poll participation'
  }, {
    icon: Heart,
    label: 'Topic preference'
  }, {
    icon: Smartphone,
    label: 'Engagement by channel'
  }, {
    icon: TrendingUp,
    label: 'Campaign-to-campaign improvement'
  }];
  return <InteriorSlide data-id="element-293" data-mp="2qov3obwtvnjl">
      <SlideHeader eyebrow="Monitoring & Optimization" data-id="element-294" data-mp="2qp4s3v285mn6">
        Every campaign creates data for the next campaign
      </SlideHeader>

      <div className="flex-1 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-6xl mx-auto w-full" data-id="element-295" data-mp="2lwlh95ux95m2">
        {/* Left: Data Loop */}
        <div className="flex-1 w-full" data-id="element-296" data-mp="2cxq7xkwkzfej">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="bg-[var(--surface)] p-8 rounded-3xl border border-black/5 relative" data-id="element-297" data-mp="2cyrizpdlk1w9">
            <div className="flex flex-col items-center gap-6 relative z-10" data-id="element-298" data-mp="2vmwl6selvesy">
              <div className="bg-white px-6 py-3 rounded-xl shadow-sm font-bold text-[var(--fg)] border border-black/5 w-48 text-center" data-id="element-299" data-mp="1p8f4twrrd8x8">
                Campaign 1
              </div>

              <div className="h-8 w-0.5 bg-[var(--brand)]/30" data-id="element-300" data-mp="1lq7w8osthuc9" />

              <div className="bg-[var(--brand)] text-white px-6 py-3 rounded-xl shadow-md font-bold w-56 text-center" data-id="element-301" data-mp="1lqrhofhof0v1">
                Engagement Data
              </div>

              <div className="h-8 w-0.5 bg-[var(--brand)]/30" data-id="element-302" data-mp="1coi4fhv0wtdy" />

              <div className="bg-white px-6 py-3 rounded-xl shadow-sm font-bold text-[var(--brand)] border-2 border-[var(--brand)] w-48 text-center" data-id="element-303" data-mp="1ho4j7vqi2hov">
                Insight
              </div>

              <div className="h-8 w-0.5 bg-[var(--brand)]/30" data-id="element-304" data-mp="17px4ljjoobq1" />

              <div className="bg-[var(--brand-light)] text-white px-6 py-3 rounded-xl shadow-md font-bold w-56 text-center" data-id="element-305" data-mp="17ri1vgxdu8rl">
                Optimization
              </div>

              <div className="h-8 w-0.5 bg-[var(--brand)]/30" data-id="element-306" data-mp="y16wdpy1vril" />

              <div className="bg-white px-6 py-3 rounded-xl shadow-sm font-bold text-[var(--fg)] border border-black/5 w-48 text-center" data-id="element-307" data-mp="k02s533a0p6b">
                Campaign 2
              </div>
            </div>

            {/* Loop connecting line */}
            <div className="absolute top-12 bottom-12 right-12 w-12 border-t-2 border-r-2 border-b-2 border-dashed border-[var(--brand)]/30 rounded-r-3xl" data-id="element-308" data-mp="5efg65788qyj" />
          </motion.div>
        </div>

        {/* Right: Metrics Grid */}
        <div className="flex-1 w-full" data-id="element-309" data-mp="f9qoo5s1l1xu">
          <motion.h3 initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-lg font-bold text-[var(--fg)] mb-6" data-id="element-310" data-mp="acqp8dpp6xfp">
            Key Metrics Captured
          </motion.h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-311" data-mp="y4noz6fvhz0g">
            {metrics.map((metric, i) => {
            const Icon = metric.icon;
            return <motion.div key={metric.label} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5 + i * 0.05,
              duration: 0.4
            }} className="bg-white p-4 rounded-xl border border-black/5 shadow-sm flex items-center gap-3" data-id="element-312" data-mp="173soaez9dwdo" data-mpi={i}>
                  <div className="h-8 w-8 rounded-lg bg-[var(--surface)] flex items-center justify-center text-[var(--brand)] flex-shrink-0" data-id="element-313" data-mp="2dliqlgyeg0oi">
                    <Icon className="h-4 w-4" data-id="element-314" data-mp="2ilet1brg7k1y" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--fg)] leading-tight" data-id="element-315" data-mp="2nj6x145dawmi">
                    {metric.label}
                  </span>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </InteriorSlide>;
}